/// <amd-module name="Feed/Common/RestClient/Feed" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   feed\client\webapi\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as Feed from "Feed/Common/Contracts/Feed";
import * as Operations from "VSS/Platform/Contracts/Operations";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";

export interface IFeedRestClient extends IVssRestClient {
    /**
     * Generate a SVG badge for the latest version of a package.  The generated SVG is typically used as the image in an HTML link which takes users to the feed containing the package to accelerate discovery and consumption.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - Id of the package (GUID Id, not name).
     * @param project - Project ID or project name
     */
    getBadge(feedId: string, packageId: string, project?: string): Promise<any>;
    /**
     * Get the capabilities for a specific feed.
     * 
     * @param feedId - a feed GUID
     * @param project - Project ID or project name
     */
    getCapabilities(feedId: string, project?: string): Promise<Feed.FeedCapabilities>;
    /**
     * Use this to change the capabilities of a feed, if possible
     * 
     * @param capabilities - New capabilities
     * @param feedId - a feed GUID.
     * @param project - Project ID or project name
     */
    updateCapability(capabilities: Feed.FeedCapabilities, feedId: string, project?: string): Promise<void>;
    /**
     * Use this to run a batch operation against the given feed
     * 
     * @param data - 
     * @param feedId - a feed GUID.
     * @param project - Project ID or project name
     */
    runBatch(data: Feed.FeedBatchData, feedId: string, project?: string): Promise<void>;
    /**
     * Query a feed to determine its current state.
     * 
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    getFeedChange(feedId: string, project?: string): Promise<Feed.FeedChange>;
    /**
     * Query to determine which feeds have changed since the last call, tracked through the provided continuationToken. Only changes to a feed itself are returned and impact the continuationToken, not additions or alterations to packages within the feeds.
     * 
     * @param project - Project ID or project name
     * @param includeDeleted - If true, get changes for all feeds including deleted feeds. The default value is false.
     * @param continuationToken - A continuation token which acts as a bookmark to a previously retrieved change. This token allows the user to continue retrieving changes in batches, picking up where the previous batch left off. If specified, all the changes that occur strictly after the token will be returned. If not specified or 0, iteration will start with the first change.
     * @param batchSize - Number of package changes to fetch. The default value is 1000. The maximum value is 2000.
     */
    getFeedChanges(project?: string, includeDeleted?: boolean, continuationToken?: number, batchSize?: number): Promise<Feed.FeedChangesResponse>;
    /**
     * Query for feeds within the recycle bin.
     * 
     * @param project - Project ID or project name
     */
    getFeedsFromRecycleBin(project?: string): Promise<Feed.Feed[]>;
    /**
     * Permanently delete a feed and all of its packages. The action is irreversible and the package content will be deleted immediately.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    permanentDeleteFeed(feedId: string, project?: string): Promise<void>;
    /**
     * Restores a deleted feed and all of its packages.
     * 
     * @param patchJson - 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    restoreDeletedFeed(patchJson: VS_Services.JsonPatchDocument, feedId: string, project?: string): Promise<void>;
    /**
     * Create a feed, a container for various package types.
     * 
     * @param feed - A JSON object containing both required and optional attributes for the feed. Name is the only required value.
     * @param project - Project ID or project name
     */
    createFeed(feed: Feed.Feed, project?: string): Promise<Feed.Feed>;
    /**
     * Remove a feed and all its packages. The feed moves to the recycle bin and is reversible.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    deleteFeed(feedId: string, project?: string): Promise<void>;
    /**
     * Get the settings for a specific feed.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param includeDeletedUpstreams - Include upstreams that have been deleted in the response.
     */
    getFeed(feedId: string, project?: string, includeDeletedUpstreams?: boolean): Promise<Feed.Feed>;
    /**
     * Get all feeds in an account where you have the provided role access.
     * 
     * @param project - Project ID or project name
     * @param feedRole - Filter by this role, either Administrator(4), Contributor(3), or Reader(2) level permissions.
     * @param includeDeletedUpstreams - Include upstreams that have been deleted in the response.
     * @param includeUrls - Resolve names if true
     */
    getFeeds(project?: string, feedRole?: Feed.FeedRole, includeDeletedUpstreams?: boolean, includeUrls?: boolean): Promise<Feed.Feed[]>;
    /**
     * Change the attributes of a feed.
     * 
     * @param feed - A JSON object containing the feed settings to be updated.
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    updateFeed(feed: Feed.FeedUpdate, feedId: string, project?: string): Promise<Feed.Feed>;
    /**
     * Get all service-wide feed creation and administration permissions.
     * 
     * @param includeIds - Set to true to add IdentityIds to the permission objects.
     */
    getGlobalPermissions(includeIds?: boolean): Promise<Feed.GlobalPermission[]>;
    /**
     * Set service-wide permissions that govern feed creation and administration.
     * 
     * @param globalPermissions - New permissions for the organization.
     */
    setGlobalPermissions(globalPermissions: Feed.GlobalPermission[]): Promise<Feed.GlobalPermission[]>;
    /**
     * Get a batch of package changes made to a feed.  The changes returned are 'most recent change' so if an Add is followed by an Update before you begin enumerating, you'll only see one change in the batch.  While consuming batches using the continuation token, you may see changes to the same package version multiple times if they are happening as you enumerate.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param continuationToken - A continuation token which acts as a bookmark to a previously retrieved change. This token allows the user to continue retrieving changes in batches, picking up where the previous batch left off. If specified, all the changes that occur strictly after the token will be returned. If not specified or 0, iteration will start with the first change.
     * @param batchSize - Number of package changes to fetch. The default value is 1000. The maximum value is 2000.
     */
    getPackageChanges(feedId: string, project?: string, continuationToken?: number, batchSize?: number): Promise<Feed.PackageChangesResponse>;
    /**
     * @param packageIdQuery - 
     * @param feedId - 
     * @param project - Project ID or project name
     */
    queryPackageMetrics(packageIdQuery: Feed.PackageMetricsQuery, feedId: string, project?: string): Promise<Feed.PackageMetrics[]>;
    /**
     * Get details about a specific package.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - The package Id (GUID Id, not the package name).
     * @param project - Project ID or project name
     * @param includeAllVersions - True to return all versions of the package in the response. Default is false (latest version only).
     * @param includeUrls - True to return REST Urls with the response. Default is True.
     * @param isListed - Only applicable for NuGet packages, setting it for other package types will result in a 404. If false, delisted package versions will be returned. Use this to filter the response when includeAllVersions is set to true. Default is unset (do not return delisted packages).
     * @param isRelease - Only applicable for Nuget packages. Use this to filter the response when includeAllVersions is set to true.  Default is True (only return packages without prerelease versioning).
     * @param includeDeleted - Return deleted or unpublished versions of packages in the response. Default is False.
     * @param includeDescription - Return the description for every version of each package in the response. Default is False.
     */
    getPackage(feedId: string, packageId: string, project?: string, includeAllVersions?: boolean, includeUrls?: boolean, isListed?: boolean, isRelease?: boolean, includeDeleted?: boolean, includeDescription?: boolean): Promise<Feed.Package>;
    /**
     * Get details about all of the packages in the feed. Use the various filters to include or exclude information from the result set.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param protocolType - One of the supported artifact package types.
     * @param packageNameQuery - Filter to packages that contain the provided string. Characters in the string must conform to the package name constraints.
     * @param normalizedPackageName - [Obsolete] Used for legacy scenarios and may be removed in future versions.
     * @param includeUrls - True to return REST Urls with the response. Default is True.
     * @param includeAllVersions - True to return all versions of the package in the response. Default is false (latest version only).
     * @param isListed - Only applicable for NuGet packages, setting it for other package types will result in a 404. If false, delisted package versions will be returned. Use this to filter the response when includeAllVersions is set to true. Default is unset (do not return delisted packages).
     * @param getTopPackageVersions - Changes the behavior of $top and $skip to return all versions of each package up to $top. Must be used in conjunction with includeAllVersions=true
     * @param isRelease - Only applicable for Nuget packages. Use this to filter the response when includeAllVersions is set to true. Default is True (only return packages without prerelease versioning).
     * @param includeDescription - Return the description for every version of each package in the response. Default is False.
     * @param top - Get the top N packages (or package versions where getTopPackageVersions=true)
     * @param skip - Skip the first N packages (or package versions where getTopPackageVersions=true)
     * @param includeDeleted - Return deleted or unpublished versions of packages in the response. Default is False.
     * @param isCached - [Obsolete] Used for legacy scenarios and may be removed in future versions.
     * @param directUpstreamId - Filter results to return packages from a specific upstream.
     */
    getPackages(feedId: string, project?: string, protocolType?: string, packageNameQuery?: string, normalizedPackageName?: string, includeUrls?: boolean, includeAllVersions?: boolean, isListed?: boolean, getTopPackageVersions?: boolean, isRelease?: boolean, includeDescription?: boolean, top?: number, skip?: number, includeDeleted?: boolean, isCached?: boolean, directUpstreamId?: string): Promise<Feed.Package[]>;
    /**
     * Get the permissions for a feed.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param includeIds - True to include user Ids in the response.  Default is false.
     * @param excludeInheritedPermissions - True to only return explicitly set permissions on the feed.  Default is false.
     * @param identityDescriptor - Filter permissions to the provided identity.
     * @param includeDeletedFeeds - If includeDeletedFeeds is true, then feedId must be specified by name and not by Guid.
     */
    getFeedPermissions(feedId: string, project?: string, includeIds?: boolean, excludeInheritedPermissions?: boolean, identityDescriptor?: string, includeDeletedFeeds?: boolean): Promise<Feed.FeedPermission[]>;
    /**
     * Update the permissions on a feed.
     * 
     * @param feedPermission - Permissions to set.
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    setFeedPermissions(feedPermission: Feed.FeedPermission[], feedId: string, project?: string): Promise<Feed.FeedPermission[]>;
    /**
     * Gets provenance for a package version.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - Id of the package (GUID Id, not name).
     * @param packageVersionId - Id of the package version (GUID Id, not name).
     * @param project - Project ID or project name
     */
    getPackageVersionProvenance(feedId: string, packageId: string, packageVersionId: string, project?: string): Promise<Feed.PackageVersionProvenance>;
    /**
     * Queues a job to remove all package versions from a feed's recycle bin
     * 
     * @param feedId - Name or Id of the feed
     * @param project - Project ID or project name
     */
    emptyRecycleBin(feedId: string, project?: string): Promise<Operations.OperationReference>;
    /**
     * Get information about a package and all its versions within the recycle bin.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - The package Id (GUID Id, not the package name).
     * @param project - Project ID or project name
     * @param includeUrls - True to return REST Urls with the response.  Default is True.
     */
    getRecycleBinPackage(feedId: string, packageId: string, project?: string, includeUrls?: boolean): Promise<Feed.Package>;
    /**
     * Query for packages within the recycle bin.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param protocolType - Type of package (e.g. NuGet, npm, ...).
     * @param packageNameQuery - Filter to packages matching this name.
     * @param includeUrls - True to return REST Urls with the response.  Default is True.
     * @param top - Get the top N packages.
     * @param skip - Skip the first N packages.
     * @param includeAllVersions - True to return all versions of the package in the response.  Default is false (latest version only).
     */
    getRecycleBinPackages(feedId: string, project?: string, protocolType?: string, packageNameQuery?: string, includeUrls?: boolean, top?: number, skip?: number, includeAllVersions?: boolean): Promise<Feed.Package[]>;
    /**
     * Get information about a package version within the recycle bin.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - The package Id (GUID Id, not the package name).
     * @param packageVersionId - The package version Id 9guid Id, not the version string).
     * @param project - Project ID or project name
     * @param includeUrls - True to return REST Urls with the response.  Default is True.
     */
    getRecycleBinPackageVersion(feedId: string, packageId: string, packageVersionId: string, project?: string, includeUrls?: boolean): Promise<Feed.RecycleBinPackageVersion>;
    /**
     * Get a list of package versions within the recycle bin.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - The package Id (GUID Id, not the package name).
     * @param project - Project ID or project name
     * @param includeUrls - True to return REST Urls with the response.  Default is True.
     */
    getRecycleBinPackageVersions(feedId: string, packageId: string, project?: string, includeUrls?: boolean): Promise<Feed.RecycleBinPackageVersion[]>;
    /**
     * @param feedId - 
     * @param packageId - 
     * @param packageVersionId - 
     * @param project - Project ID or project name
     */
    permanentlyDeletePackageVersion(feedId: string, packageId: string, packageVersionId: string, project?: string): Promise<void>;
    /**
     * @param patchJson - 
     * @param feedId - 
     * @param packageId - 
     * @param packageVersionId - 
     * @param project - Project ID or project name
     */
    updateRecycleBinPackageVersion(patchJson: VS_Services.JsonPatchDocument, feedId: string, packageId: string, packageVersionId: string, project?: string): Promise<void>;
    /**
     * Delete the retention policy for a feed.
     * 
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    deleteFeedRetentionPolicies(feedId: string, project?: string): Promise<void>;
    /**
     * Get the retention policy for a feed.
     * 
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    getFeedRetentionPolicies(feedId: string, project?: string): Promise<Feed.FeedRetentionPolicy>;
    /**
     * Set the retention policy for a feed.
     * 
     * @param policy - Feed retention policy.
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    setFeedRetentionPolicies(policy: Feed.FeedRetentionPolicy, feedId: string, project?: string): Promise<Feed.FeedRetentionPolicy>;
    /**
     * @param packageVersionIdQuery - 
     * @param feedId - 
     * @param packageId - 
     * @param project - Project ID or project name
     */
    queryPackageVersionMetrics(packageVersionIdQuery: Feed.PackageVersionMetricsQuery, feedId: string, packageId: string, project?: string): Promise<Feed.PackageVersionMetrics[]>;
    /**
     * @param feedId - 
     * @param packageId - 
     * @param packageVersionId - 
     * @param deletedDate - 
     * @param scheduledPermanentDeleteDate - 
     * @param project - Project ID or project name
     */
    deletePackageVersion(feedId: string, packageId: string, packageVersionId: string, deletedDate: Date, scheduledPermanentDeleteDate: Date, project?: string): Promise<void>;
    /**
     * Get details about a specific package version.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - Id of the package (GUID Id, not name).
     * @param packageVersionId - Id of the package version (GUID Id, not name).
     * @param project - Project ID or project name
     * @param includeUrls - True to include urls for each version. Default is true.
     * @param isListed - Only applicable for NuGet packages. If false, delisted package versions will be returned.
     * @param isDeleted - This does not have any effect on the requested package version, for other versions returned specifies whether to return only deleted or non-deleted versions of packages in the response. Default is unset (return all versions).
     */
    getPackageVersion(feedId: string, packageId: string, packageVersionId: string, project?: string, includeUrls?: boolean, isListed?: boolean, isDeleted?: boolean): Promise<Feed.PackageVersion>;
    /**
     * Get a list of package versions, optionally filtering by state.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - Id of the package (GUID Id, not name).
     * @param project - Project ID or project name
     * @param includeUrls - True to include urls for each version. Default is true.
     * @param isListed - Only applicable for NuGet packages. If false, delisted package versions will be returned.
     * @param isDeleted - If set specifies whether to return only deleted or non-deleted versions of packages in the response. Default is unset (return all versions).
     */
    getPackageVersions(feedId: string, packageId: string, project?: string, includeUrls?: boolean, isListed?: boolean, isDeleted?: boolean): Promise<Feed.PackageVersion[]>;
    /**
     * @param patchJson - 
     * @param feedId - 
     * @param packageId - 
     * @param packageVersionId - 
     * @param project - Project ID or project name
     */
    updatePackageVersion(patchJson: VS_Services.JsonPatchDocument, feedId: string, packageId: string, packageVersionId: string, project?: string): Promise<void>;
    /**
     * Create a new view on the referenced feed.
     * 
     * @param view - View to be created.
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    createFeedView(view: Feed.FeedView, feedId: string, project?: string): Promise<Feed.FeedView>;
    /**
     * Delete a feed view.
     * 
     * @param feedId - Name or Id of the feed.
     * @param viewId - Name or Id of the view.
     * @param project - Project ID or project name
     */
    deleteFeedView(feedId: string, viewId: string, project?: string): Promise<void>;
    /**
     * Get a view by Id.
     * 
     * @param feedId - Name or Id of the feed.
     * @param viewId - Name or Id of the view.
     * @param project - Project ID or project name
     */
    getFeedView(feedId: string, viewId: string, project?: string): Promise<Feed.FeedView>;
    /**
     * Get all views for a feed.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    getFeedViews(feedId: string, project?: string): Promise<Feed.FeedView[]>;
    /**
     * Update a view.
     * 
     * @param view - New settings to apply to the specified view.
     * @param feedId - Name or Id of the feed.
     * @param viewId - Name or Id of the view.
     * @param project - Project ID or project name
     */
    updateFeedView(view: Feed.FeedView, feedId: string, viewId: string, project?: string): Promise<Feed.FeedView>;
}

class FeedRestClient extends RestClientBase implements IFeedRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Generate a SVG badge for the latest version of a package.  The generated SVG is typically used as the image in an HTML link which takes users to the feed containing the package to accelerate discovery and consumption.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - Id of the package (GUID Id, not name).
     * @param project - Project ID or project name
     */
    public async getBadge(
        feedId: string,
        packageId: string,
        project?: string
        ): Promise<any> {

        return this.beginRequest<any>({
            apiVersion: "5.1-preview.1",
            httpResponseType: "image/svg+xml",
            routeTemplate: "{project}/_apis/public/Packaging/Feeds/{feedId}/Packages/{packageId}/Badge",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId
            }
        });
    }

    /**
     * Get the capabilities for a specific feed.
     * 
     * @param feedId - a feed GUID
     * @param project - Project ID or project name
     */
    public async getCapabilities(
        feedId: string,
        project?: string
        ): Promise<Feed.FeedCapabilities> {

        return this.beginRequest<Feed.FeedCapabilities>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Capabilities",
            routeValues: {
                project: project,
                feedId: feedId
            }
        });
    }

    /**
     * Use this to change the capabilities of a feed, if possible
     * 
     * @param capabilities - New capabilities
     * @param feedId - a feed GUID.
     * @param project - Project ID or project name
     */
    public async updateCapability(
        capabilities: Feed.FeedCapabilities,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "PUT",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Capabilities",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: capabilities
        });
    }

    /**
     * Use this to run a batch operation against the given feed
     * 
     * @param data - 
     * @param feedId - a feed GUID.
     * @param project - Project ID or project name
     */
    public async runBatch(
        data: Feed.FeedBatchData,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "PUT",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/FeedBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: data
        });
    }

    /**
     * Query a feed to determine its current state.
     * 
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async getFeedChange(
        feedId: string,
        project?: string
        ): Promise<Feed.FeedChange> {

        return this.beginRequest<Feed.FeedChange>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/FeedChanges/{feedId}",
            routeValues: {
                project: project,
                feedId: feedId
            }
        });
    }

    /**
     * Query to determine which feeds have changed since the last call, tracked through the provided continuationToken. Only changes to a feed itself are returned and impact the continuationToken, not additions or alterations to packages within the feeds.
     * 
     * @param project - Project ID or project name
     * @param includeDeleted - If true, get changes for all feeds including deleted feeds. The default value is false.
     * @param continuationToken - A continuation token which acts as a bookmark to a previously retrieved change. This token allows the user to continue retrieving changes in batches, picking up where the previous batch left off. If specified, all the changes that occur strictly after the token will be returned. If not specified or 0, iteration will start with the first change.
     * @param batchSize - Number of package changes to fetch. The default value is 1000. The maximum value is 2000.
     */
    public async getFeedChanges(
        project?: string,
        includeDeleted?: boolean,
        continuationToken?: number,
        batchSize?: number
        ): Promise<Feed.FeedChangesResponse> {

        const queryValues: any = {
            includeDeleted: includeDeleted,
            continuationToken: continuationToken,
            batchSize: batchSize
        };

        return this.beginRequest<Feed.FeedChangesResponse>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/FeedChanges/{feedId}",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

    /**
     * Query for feeds within the recycle bin.
     * 
     * @param project - Project ID or project name
     */
    public async getFeedsFromRecycleBin(
        project?: string
        ): Promise<Feed.Feed[]> {

        return this.beginRequest<Feed.Feed[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/FeedRecycleBin/{feedId}",
            routeValues: {
                project: project
            }
        });
    }

    /**
     * Permanently delete a feed and all of its packages. The action is irreversible and the package content will be deleted immediately.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    public async permanentDeleteFeed(
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/Packaging/FeedRecycleBin/{feedId}",
            routeValues: {
                project: project,
                feedId: feedId
            }
        });
    }

    /**
     * Restores a deleted feed and all of its packages.
     * 
     * @param patchJson - 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    public async restoreDeletedFeed(
        patchJson: VS_Services.JsonPatchDocument,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/Packaging/FeedRecycleBin/{feedId}",
            routeValues: {
                project: project,
                feedId: feedId
            },
            customHeaders: {
                "Content-Type": "application/json-patch+json",
            },
            body: patchJson
        });
    }

    /**
     * Create a feed, a container for various package types.
     * 
     * @param feed - A JSON object containing both required and optional attributes for the feed. Name is the only required value.
     * @param project - Project ID or project name
     */
    public async createFeed(
        feed: Feed.Feed,
        project?: string
        ): Promise<Feed.Feed> {

        return this.beginRequest<Feed.Feed>({
            apiVersion: "5.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}",
            routeValues: {
                project: project
            },
            body: feed
        });
    }

    /**
     * Remove a feed and all its packages. The feed moves to the recycle bin and is reversible.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    public async deleteFeed(
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}",
            routeValues: {
                project: project,
                feedId: feedId
            }
        });
    }

    /**
     * Get the settings for a specific feed.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param includeDeletedUpstreams - Include upstreams that have been deleted in the response.
     */
    public async getFeed(
        feedId: string,
        project?: string,
        includeDeletedUpstreams?: boolean
        ): Promise<Feed.Feed> {

        const queryValues: any = {
            includeDeletedUpstreams: includeDeletedUpstreams
        };

        return this.beginRequest<Feed.Feed>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}",
            routeValues: {
                project: project,
                feedId: feedId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get all feeds in an account where you have the provided role access.
     * 
     * @param project - Project ID or project name
     * @param feedRole - Filter by this role, either Administrator(4), Contributor(3), or Reader(2) level permissions.
     * @param includeDeletedUpstreams - Include upstreams that have been deleted in the response.
     * @param includeUrls - Resolve names if true
     */
    public async getFeeds(
        project?: string,
        feedRole?: Feed.FeedRole,
        includeDeletedUpstreams?: boolean,
        includeUrls?: boolean
        ): Promise<Feed.Feed[]> {

        const queryValues: any = {
            feedRole: feedRole,
            includeDeletedUpstreams: includeDeletedUpstreams,
            includeUrls: includeUrls
        };

        return this.beginRequest<Feed.Feed[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

    /**
     * Change the attributes of a feed.
     * 
     * @param feed - A JSON object containing the feed settings to be updated.
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    public async updateFeed(
        feed: Feed.FeedUpdate,
        feedId: string,
        project?: string
        ): Promise<Feed.Feed> {

        return this.beginRequest<Feed.Feed>({
            apiVersion: "5.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: feed
        });
    }

    /**
     * Get all service-wide feed creation and administration permissions.
     * 
     * @param includeIds - Set to true to add IdentityIds to the permission objects.
     */
    public async getGlobalPermissions(
        includeIds?: boolean
        ): Promise<Feed.GlobalPermission[]> {

        const queryValues: any = {
            includeIds: includeIds
        };

        return this.beginRequest<Feed.GlobalPermission[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "_apis/Packaging/GlobalPermissions",
            queryParams: queryValues
        });
    }

    /**
     * Set service-wide permissions that govern feed creation and administration.
     * 
     * @param globalPermissions - New permissions for the organization.
     */
    public async setGlobalPermissions(
        globalPermissions: Feed.GlobalPermission[]
        ): Promise<Feed.GlobalPermission[]> {

        return this.beginRequest<Feed.GlobalPermission[]>({
            apiVersion: "5.1-preview.1",
            method: "PATCH",
            routeTemplate: "_apis/Packaging/GlobalPermissions",
            body: globalPermissions
        });
    }

    /**
     * Get a batch of package changes made to a feed.  The changes returned are 'most recent change' so if an Add is followed by an Update before you begin enumerating, you'll only see one change in the batch.  While consuming batches using the continuation token, you may see changes to the same package version multiple times if they are happening as you enumerate.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param continuationToken - A continuation token which acts as a bookmark to a previously retrieved change. This token allows the user to continue retrieving changes in batches, picking up where the previous batch left off. If specified, all the changes that occur strictly after the token will be returned. If not specified or 0, iteration will start with the first change.
     * @param batchSize - Number of package changes to fetch. The default value is 1000. The maximum value is 2000.
     */
    public async getPackageChanges(
        feedId: string,
        project?: string,
        continuationToken?: number,
        batchSize?: number
        ): Promise<Feed.PackageChangesResponse> {

        const queryValues: any = {
            continuationToken: continuationToken,
            batchSize: batchSize
        };

        return this.beginRequest<Feed.PackageChangesResponse>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/PackageChanges",
            routeValues: {
                project: project,
                feedId: feedId
            },
            queryParams: queryValues
        });
    }

    /**
     * @param packageIdQuery - 
     * @param feedId - 
     * @param project - Project ID or project name
     */
    public async queryPackageMetrics(
        packageIdQuery: Feed.PackageMetricsQuery,
        feedId: string,
        project?: string
        ): Promise<Feed.PackageMetrics[]> {

        return this.beginRequest<Feed.PackageMetrics[]>({
            apiVersion: "5.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/PackageMetricsBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: packageIdQuery
        });
    }

    /**
     * Get details about a specific package.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - The package Id (GUID Id, not the package name).
     * @param project - Project ID or project name
     * @param includeAllVersions - True to return all versions of the package in the response. Default is false (latest version only).
     * @param includeUrls - True to return REST Urls with the response. Default is True.
     * @param isListed - Only applicable for NuGet packages, setting it for other package types will result in a 404. If false, delisted package versions will be returned. Use this to filter the response when includeAllVersions is set to true. Default is unset (do not return delisted packages).
     * @param isRelease - Only applicable for Nuget packages. Use this to filter the response when includeAllVersions is set to true.  Default is True (only return packages without prerelease versioning).
     * @param includeDeleted - Return deleted or unpublished versions of packages in the response. Default is False.
     * @param includeDescription - Return the description for every version of each package in the response. Default is False.
     */
    public async getPackage(
        feedId: string,
        packageId: string,
        project?: string,
        includeAllVersions?: boolean,
        includeUrls?: boolean,
        isListed?: boolean,
        isRelease?: boolean,
        includeDeleted?: boolean,
        includeDescription?: boolean
        ): Promise<Feed.Package> {

        const queryValues: any = {
            includeAllVersions: includeAllVersions,
            includeUrls: includeUrls,
            isListed: isListed,
            isRelease: isRelease,
            includeDeleted: includeDeleted,
            includeDescription: includeDescription
        };

        return this.beginRequest<Feed.Package>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Packages/{packageId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get details about all of the packages in the feed. Use the various filters to include or exclude information from the result set.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param protocolType - One of the supported artifact package types.
     * @param packageNameQuery - Filter to packages that contain the provided string. Characters in the string must conform to the package name constraints.
     * @param normalizedPackageName - [Obsolete] Used for legacy scenarios and may be removed in future versions.
     * @param includeUrls - True to return REST Urls with the response. Default is True.
     * @param includeAllVersions - True to return all versions of the package in the response. Default is false (latest version only).
     * @param isListed - Only applicable for NuGet packages, setting it for other package types will result in a 404. If false, delisted package versions will be returned. Use this to filter the response when includeAllVersions is set to true. Default is unset (do not return delisted packages).
     * @param getTopPackageVersions - Changes the behavior of $top and $skip to return all versions of each package up to $top. Must be used in conjunction with includeAllVersions=true
     * @param isRelease - Only applicable for Nuget packages. Use this to filter the response when includeAllVersions is set to true. Default is True (only return packages without prerelease versioning).
     * @param includeDescription - Return the description for every version of each package in the response. Default is False.
     * @param top - Get the top N packages (or package versions where getTopPackageVersions=true)
     * @param skip - Skip the first N packages (or package versions where getTopPackageVersions=true)
     * @param includeDeleted - Return deleted or unpublished versions of packages in the response. Default is False.
     * @param isCached - [Obsolete] Used for legacy scenarios and may be removed in future versions.
     * @param directUpstreamId - Filter results to return packages from a specific upstream.
     */
    public async getPackages(
        feedId: string,
        project?: string,
        protocolType?: string,
        packageNameQuery?: string,
        normalizedPackageName?: string,
        includeUrls?: boolean,
        includeAllVersions?: boolean,
        isListed?: boolean,
        getTopPackageVersions?: boolean,
        isRelease?: boolean,
        includeDescription?: boolean,
        top?: number,
        skip?: number,
        includeDeleted?: boolean,
        isCached?: boolean,
        directUpstreamId?: string
        ): Promise<Feed.Package[]> {

        const queryValues: any = {
            protocolType: protocolType,
            packageNameQuery: packageNameQuery,
            normalizedPackageName: normalizedPackageName,
            includeUrls: includeUrls,
            includeAllVersions: includeAllVersions,
            isListed: isListed,
            getTopPackageVersions: getTopPackageVersions,
            isRelease: isRelease,
            includeDescription: includeDescription,
            '$top': top,
            '$skip': skip,
            includeDeleted: includeDeleted,
            isCached: isCached,
            directUpstreamId: directUpstreamId
        };

        return this.beginRequest<Feed.Package[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Packages/{packageId}",
            routeValues: {
                project: project,
                feedId: feedId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get the permissions for a feed.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param includeIds - True to include user Ids in the response.  Default is false.
     * @param excludeInheritedPermissions - True to only return explicitly set permissions on the feed.  Default is false.
     * @param identityDescriptor - Filter permissions to the provided identity.
     * @param includeDeletedFeeds - If includeDeletedFeeds is true, then feedId must be specified by name and not by Guid.
     */
    public async getFeedPermissions(
        feedId: string,
        project?: string,
        includeIds?: boolean,
        excludeInheritedPermissions?: boolean,
        identityDescriptor?: string,
        includeDeletedFeeds?: boolean
        ): Promise<Feed.FeedPermission[]> {

        const queryValues: any = {
            includeIds: includeIds,
            excludeInheritedPermissions: excludeInheritedPermissions,
            identityDescriptor: identityDescriptor,
            includeDeletedFeeds: includeDeletedFeeds
        };

        return this.beginRequest<Feed.FeedPermission[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Permissions",
            routeValues: {
                project: project,
                feedId: feedId
            },
            queryParams: queryValues
        });
    }

    /**
     * Update the permissions on a feed.
     * 
     * @param feedPermission - Permissions to set.
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    public async setFeedPermissions(
        feedPermission: Feed.FeedPermission[],
        feedId: string,
        project?: string
        ): Promise<Feed.FeedPermission[]> {

        return this.beginRequest<Feed.FeedPermission[]>({
            apiVersion: "5.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Permissions",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: feedPermission
        });
    }

    /**
     * Gets provenance for a package version.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - Id of the package (GUID Id, not name).
     * @param packageVersionId - Id of the package version (GUID Id, not name).
     * @param project - Project ID or project name
     */
    public async getPackageVersionProvenance(
        feedId: string,
        packageId: string,
        packageVersionId: string,
        project?: string
        ): Promise<Feed.PackageVersionProvenance> {

        return this.beginRequest<Feed.PackageVersionProvenance>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Packages/{packageId}/Versions/{packageVersionId}/Provenance",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId,
                packageVersionId: packageVersionId
            }
        });
    }

    /**
     * Queues a job to remove all package versions from a feed's recycle bin
     * 
     * @param feedId - Name or Id of the feed
     * @param project - Project ID or project name
     */
    public async emptyRecycleBin(
        feedId: string,
        project?: string
        ): Promise<Operations.OperationReference> {

        return this.beginRequest<Operations.OperationReference>({
            apiVersion: "5.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RecycleBin/Packages/{packageId}",
            routeValues: {
                project: project,
                feedId: feedId
            }
        });
    }

    /**
     * Get information about a package and all its versions within the recycle bin.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - The package Id (GUID Id, not the package name).
     * @param project - Project ID or project name
     * @param includeUrls - True to return REST Urls with the response.  Default is True.
     */
    public async getRecycleBinPackage(
        feedId: string,
        packageId: string,
        project?: string,
        includeUrls?: boolean
        ): Promise<Feed.Package> {

        const queryValues: any = {
            includeUrls: includeUrls
        };

        return this.beginRequest<Feed.Package>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RecycleBin/Packages/{packageId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId
            },
            queryParams: queryValues
        });
    }

    /**
     * Query for packages within the recycle bin.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     * @param protocolType - Type of package (e.g. NuGet, npm, ...).
     * @param packageNameQuery - Filter to packages matching this name.
     * @param includeUrls - True to return REST Urls with the response.  Default is True.
     * @param top - Get the top N packages.
     * @param skip - Skip the first N packages.
     * @param includeAllVersions - True to return all versions of the package in the response.  Default is false (latest version only).
     */
    public async getRecycleBinPackages(
        feedId: string,
        project?: string,
        protocolType?: string,
        packageNameQuery?: string,
        includeUrls?: boolean,
        top?: number,
        skip?: number,
        includeAllVersions?: boolean
        ): Promise<Feed.Package[]> {

        const queryValues: any = {
            protocolType: protocolType,
            packageNameQuery: packageNameQuery,
            includeUrls: includeUrls,
            '$top': top,
            '$skip': skip,
            includeAllVersions: includeAllVersions
        };

        return this.beginRequest<Feed.Package[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RecycleBin/Packages/{packageId}",
            routeValues: {
                project: project,
                feedId: feedId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get information about a package version within the recycle bin.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - The package Id (GUID Id, not the package name).
     * @param packageVersionId - The package version Id 9guid Id, not the version string).
     * @param project - Project ID or project name
     * @param includeUrls - True to return REST Urls with the response.  Default is True.
     */
    public async getRecycleBinPackageVersion(
        feedId: string,
        packageId: string,
        packageVersionId: string,
        project?: string,
        includeUrls?: boolean
        ): Promise<Feed.RecycleBinPackageVersion> {

        const queryValues: any = {
            includeUrls: includeUrls
        };

        return this.beginRequest<Feed.RecycleBinPackageVersion>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RecycleBin/Packages/{packageId}/Versions/{packageVersionId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId,
                packageVersionId: packageVersionId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get a list of package versions within the recycle bin.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - The package Id (GUID Id, not the package name).
     * @param project - Project ID or project name
     * @param includeUrls - True to return REST Urls with the response.  Default is True.
     */
    public async getRecycleBinPackageVersions(
        feedId: string,
        packageId: string,
        project?: string,
        includeUrls?: boolean
        ): Promise<Feed.RecycleBinPackageVersion[]> {

        const queryValues: any = {
            includeUrls: includeUrls
        };

        return this.beginRequest<Feed.RecycleBinPackageVersion[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RecycleBin/Packages/{packageId}/Versions/{packageVersionId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId
            },
            queryParams: queryValues
        });
    }

    /**
     * @param feedId - 
     * @param packageId - 
     * @param packageVersionId - 
     * @param project - Project ID or project name
     */
    public async permanentlyDeletePackageVersion(
        feedId: string,
        packageId: string,
        packageVersionId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RecycleBin/Packages/{packageId}/Versions/{packageVersionId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId,
                packageVersionId: packageVersionId
            }
        });
    }

    /**
     * @param patchJson - 
     * @param feedId - 
     * @param packageId - 
     * @param packageVersionId - 
     * @param project - Project ID or project name
     */
    public async updateRecycleBinPackageVersion(
        patchJson: VS_Services.JsonPatchDocument,
        feedId: string,
        packageId: string,
        packageVersionId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RecycleBin/Packages/{packageId}/Versions/{packageVersionId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId,
                packageVersionId: packageVersionId
            },
            customHeaders: {
                "Content-Type": "application/json-patch+json",
            },
            body: patchJson
        });
    }

    /**
     * Delete the retention policy for a feed.
     * 
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async deleteFeedRetentionPolicies(
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RetentionPolicies",
            routeValues: {
                project: project,
                feedId: feedId
            }
        });
    }

    /**
     * Get the retention policy for a feed.
     * 
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async getFeedRetentionPolicies(
        feedId: string,
        project?: string
        ): Promise<Feed.FeedRetentionPolicy> {

        return this.beginRequest<Feed.FeedRetentionPolicy>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RetentionPolicies",
            routeValues: {
                project: project,
                feedId: feedId
            }
        });
    }

    /**
     * Set the retention policy for a feed.
     * 
     * @param policy - Feed retention policy.
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async setFeedRetentionPolicies(
        policy: Feed.FeedRetentionPolicy,
        feedId: string,
        project?: string
        ): Promise<Feed.FeedRetentionPolicy> {

        return this.beginRequest<Feed.FeedRetentionPolicy>({
            apiVersion: "5.1-preview.1",
            method: "PUT",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/RetentionPolicies",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: policy
        });
    }

    /**
     * @param packageVersionIdQuery - 
     * @param feedId - 
     * @param packageId - 
     * @param project - Project ID or project name
     */
    public async queryPackageVersionMetrics(
        packageVersionIdQuery: Feed.PackageVersionMetricsQuery,
        feedId: string,
        packageId: string,
        project?: string
        ): Promise<Feed.PackageVersionMetrics[]> {

        return this.beginRequest<Feed.PackageVersionMetrics[]>({
            apiVersion: "5.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Packages/{packageId}/VersionMetricsBatch",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId
            },
            body: packageVersionIdQuery
        });
    }

    /**
     * @param feedId - 
     * @param packageId - 
     * @param packageVersionId - 
     * @param deletedDate - 
     * @param scheduledPermanentDeleteDate - 
     * @param project - Project ID or project name
     */
    public async deletePackageVersion(
        feedId: string,
        packageId: string,
        packageVersionId: string,
        deletedDate: Date,
        scheduledPermanentDeleteDate: Date,
        project?: string
        ): Promise<void> {

        const queryValues: any = {
            deletedDate: deletedDate,
            scheduledPermanentDeleteDate: scheduledPermanentDeleteDate
        };

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Packages/{packageId}/Versions/{packageVersionId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId,
                packageVersionId: packageVersionId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get details about a specific package version.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - Id of the package (GUID Id, not name).
     * @param packageVersionId - Id of the package version (GUID Id, not name).
     * @param project - Project ID or project name
     * @param includeUrls - True to include urls for each version. Default is true.
     * @param isListed - Only applicable for NuGet packages. If false, delisted package versions will be returned.
     * @param isDeleted - This does not have any effect on the requested package version, for other versions returned specifies whether to return only deleted or non-deleted versions of packages in the response. Default is unset (return all versions).
     */
    public async getPackageVersion(
        feedId: string,
        packageId: string,
        packageVersionId: string,
        project?: string,
        includeUrls?: boolean,
        isListed?: boolean,
        isDeleted?: boolean
        ): Promise<Feed.PackageVersion> {

        const queryValues: any = {
            includeUrls: includeUrls,
            isListed: isListed,
            isDeleted: isDeleted
        };

        return this.beginRequest<Feed.PackageVersion>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Packages/{packageId}/Versions/{packageVersionId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId,
                packageVersionId: packageVersionId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get a list of package versions, optionally filtering by state.
     * 
     * @param feedId - Name or Id of the feed.
     * @param packageId - Id of the package (GUID Id, not name).
     * @param project - Project ID or project name
     * @param includeUrls - True to include urls for each version. Default is true.
     * @param isListed - Only applicable for NuGet packages. If false, delisted package versions will be returned.
     * @param isDeleted - If set specifies whether to return only deleted or non-deleted versions of packages in the response. Default is unset (return all versions).
     */
    public async getPackageVersions(
        feedId: string,
        packageId: string,
        project?: string,
        includeUrls?: boolean,
        isListed?: boolean,
        isDeleted?: boolean
        ): Promise<Feed.PackageVersion[]> {

        const queryValues: any = {
            includeUrls: includeUrls,
            isListed: isListed,
            isDeleted: isDeleted
        };

        return this.beginRequest<Feed.PackageVersion[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Packages/{packageId}/Versions/{packageVersionId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId
            },
            queryParams: queryValues
        });
    }

    /**
     * @param patchJson - 
     * @param feedId - 
     * @param packageId - 
     * @param packageVersionId - 
     * @param project - Project ID or project name
     */
    public async updatePackageVersion(
        patchJson: VS_Services.JsonPatchDocument,
        feedId: string,
        packageId: string,
        packageVersionId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Packages/{packageId}/Versions/{packageVersionId}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageId: packageId,
                packageVersionId: packageVersionId
            },
            customHeaders: {
                "Content-Type": "application/json-patch+json",
            },
            body: patchJson
        });
    }

    /**
     * Create a new view on the referenced feed.
     * 
     * @param view - View to be created.
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    public async createFeedView(
        view: Feed.FeedView,
        feedId: string,
        project?: string
        ): Promise<Feed.FeedView> {

        return this.beginRequest<Feed.FeedView>({
            apiVersion: "5.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Views/{viewId}",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: view
        });
    }

    /**
     * Delete a feed view.
     * 
     * @param feedId - Name or Id of the feed.
     * @param viewId - Name or Id of the view.
     * @param project - Project ID or project name
     */
    public async deleteFeedView(
        feedId: string,
        viewId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.1-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Views/{viewId}",
            routeValues: {
                project: project,
                feedId: feedId,
                viewId: viewId
            }
        });
    }

    /**
     * Get a view by Id.
     * 
     * @param feedId - Name or Id of the feed.
     * @param viewId - Name or Id of the view.
     * @param project - Project ID or project name
     */
    public async getFeedView(
        feedId: string,
        viewId: string,
        project?: string
        ): Promise<Feed.FeedView> {

        return this.beginRequest<Feed.FeedView>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Views/{viewId}",
            routeValues: {
                project: project,
                feedId: feedId,
                viewId: viewId
            }
        });
    }

    /**
     * Get all views for a feed.
     * 
     * @param feedId - Name or Id of the feed.
     * @param project - Project ID or project name
     */
    public async getFeedViews(
        feedId: string,
        project?: string
        ): Promise<Feed.FeedView[]> {

        return this.beginRequest<Feed.FeedView[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Views/{viewId}",
            routeValues: {
                project: project,
                feedId: feedId
            }
        });
    }

    /**
     * Update a view.
     * 
     * @param view - New settings to apply to the specified view.
     * @param feedId - Name or Id of the feed.
     * @param viewId - Name or Id of the view.
     * @param project - Project ID or project name
     */
    public async updateFeedView(
        view: Feed.FeedView,
        feedId: string,
        viewId: string,
        project?: string
        ): Promise<Feed.FeedView> {

        return this.beginRequest<Feed.FeedView>({
            apiVersion: "5.1-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/Packaging/Feeds/{feedId}/Views/{viewId}",
            routeValues: {
                project: project,
                feedId: feedId,
                viewId: viewId
            },
            body: view
        });
    }

}

export const FeedClientName = "Feed.IFeedRestClient";

export function getFeedClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IFeedRestClient {
    return pageContext.getRestClient<IFeedRestClient>(FeedClientName, options);
}

RestClients.add(FeedClientName, { 
    factory: FeedRestClient,
    options: {
        resourceAreaId: "7ab4e64e-c4d8-4f50-ae73-5ef2e21642a5",
        serviceInstanceType: "00000036-0000-8888-8000-000000000000"
    }
});
